import { Mail, MessageSquare, Phone } from 'lucide-react';

export interface InboxTab {
  value: string;
  label: string;
  countKey: 'unread' | 'all';
  tooltip: string;
  headerTitle: string;
  showBadge: boolean;
}

export const INBOX_TABS: InboxTab[] = [
  {
    value: 'unread',
    label: 'Unread',
    countKey: 'unread',
    tooltip: 'Messages you have not read yet',
    headerTitle: 'Unread Messages',
    showBadge: true,
  },
  {
    value: 'all',
    label: 'All',
    countKey: 'all',
    tooltip: 'All messages in your inbox',
    headerTitle: 'Inbox',
    showBadge: false,
  },
];

export function parseInboxTab(value: string | null | undefined): string {
  if (value && INBOX_TABS.some((t) => t.value === value)) return value;
  return 'unread';
}

export function buildInboxHref(tab?: string): string {
  return tab && tab !== 'unread' ? `/inbox?tab=${tab}` : '/inbox';
}

// ─── Channel display helpers (shared by DashboardPage + InboxPage) ───────────

export const CHANNEL_STYLES: Record<
  string,
  { label: string; bg: string; icon?: 'sms' | 'email' | 'whatsapp' | 'call' }
> = {
  sms_received:   { label: 'SMS',      bg: 'bg-blue-100 text-blue-700',   icon: 'sms' },
  sms_sent:       { label: 'SMS',      bg: 'bg-blue-100 text-blue-700',   icon: 'sms' },
  email_received: { label: 'EMAIL',    bg: 'bg-purple-100 text-purple-700', icon: 'email' },
  email_sent:     { label: 'EMAIL',    bg: 'bg-purple-100 text-purple-700', icon: 'email' },
  call_logged:    { label: 'CALL',     bg: 'bg-orange-100 text-orange-700', icon: 'call' },
  chat:           { label: 'WHATSAPP', bg: 'bg-green-100 text-green-700', icon: 'whatsapp' },
  note:           { label: 'NOTE',     bg: 'bg-gray-100 text-gray-700' },
};

/**
 * Options for the Inbox "Type" sidebar filter.
 *
 * Each value is a direction-agnostic channel token the API resolves into a
 * set of legacy typeIDs (CommLogController::legacyTypeIdsForFilter), grouped
 * so a ticked option matches exactly the rows whose Type badge carries that
 * label — 'sms' therefore covers WhatsApp-era typeIDs too, because the badge
 * renders those as SMS. Stage changes are omitted: they are system rows that
 * never land in a user's inbox feed.
 */
export const INBOX_TYPE_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'call', label: 'Call' },
  { value: 'note', label: 'Note' },
  { value: 'document', label: 'Document' },
];

export function channelIcon(icon?: string) {
  if (icon === 'email') return <Mail className="h-3.5 w-3.5" />;
  if (icon === 'call') return <Phone className="h-3.5 w-3.5" />;
  if (icon === 'whatsapp') return <MessageSquare className="h-3.5 w-3.5 text-green-500" />;
  return <MessageSquare className="h-3.5 w-3.5" />;
}

export function relativeTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, now - then);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}
